import { useEffect, useState } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';

const NO_ACTIVE_MENU_VARIANT_KEY = 'none';

interface IResolvedMenuExperimentVariant {
  exposureFlagKey: string;
  exposurePathname: string | undefined;
  isPending: false;
  variantName: string | undefined;
  variantNames: string[];
}

interface IPendingMenuExperimentVariant {
  exposureFlagKey?: undefined;
  exposurePathname?: undefined;
  isPending: true;
  variantName?: undefined;
  variantNames?: undefined;
}

/** Represents either a resolved menu assignment or an unresolved Amplitude evaluation. */
export type IMenuExperimentVariant = IResolvedMenuExperimentVariant | IPendingMenuExperimentVariant;

interface IUseMenuExperimentVariantOptions {
  isEnabled?: boolean;
}

const getMenuVariantNames = (...names: Array<string | undefined>): string[] =>
  Array.from(
    new Set(
      names.filter((name): name is string => Boolean(name) && name !== NO_ACTIVE_MENU_VARIANT_KEY)
    )
  );

/**
 * Returns the assignment for the ongoing Menu A/B experiment, if any.
 *
 * The `OngoingMenuExperiment` flag on Amplitude tell us what's the ongoing
 * experiment that is currently running.
 *
 * The ongoing experiment has the `variantName` that's used to match
 * the menu content from Sanity and the StoreMenu.
 * Different users might get different values depending if they are on
 * Control or Treatment groups.
 *
 * @returns The ongoing menu variant and the flag that assigned it, if any.
 */
export const useMenuExperimentVariant = ({
  isEnabled = true,
}: IUseMenuExperimentVariantOptions = {}): IMenuExperimentVariant | undefined => {
  const {
    getVariantValue,
    hasAssignmentFetchFailed,
    isAssignmentLoading,
    loadingCycle,
    assignmentLoadingDeadline,
  } = useExperiments();
  const [timedOutLoadingCycle, setTimedOutLoadingCycle] = useState<number>();
  const hasLoadingDeadlineElapsed = Date.now() >= assignmentLoadingDeadline;

  useEffect(() => {
    if (!isEnabled || !isAssignmentLoading || hasLoadingDeadlineElapsed) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setTimedOutLoadingCycle(loadingCycle);
    }, Math.max(0, assignmentLoadingDeadline - Date.now()));

    return () => clearTimeout(timeoutId);
  }, [
    hasLoadingDeadlineElapsed,
    isEnabled,
    isAssignmentLoading,
    loadingCycle,
    assignmentLoadingDeadline,
  ]);

  const hasPendingTimedOut = hasLoadingDeadlineElapsed || timedOutLoadingCycle === loadingCycle;

  if (!isEnabled) {
    return;
  }

  // A loading cycle can retain the previous assignment while Amplitude refreshes the current
  // identity. Do not publish that cached menu as current: exposure reporting is suppressed during
  // the same cycle, so displaying it could leave the rendered assignment without an exposure.
  if (isAssignmentLoading) {
    return hasPendingTimedOut ? undefined : { isPending: true };
  }

  // Ask Amplitude what's the ongoing experiment or directly assigned menu variant.
  const { key: directMenuVariantKey, payload, value: menuExperimentKey } =
    getVariantValue(RunningExperiments.OngoingMenuExperiment) || {};
  const exposurePathname = typeof payload?.pathname === 'string' ? payload.pathname : undefined;

  if (!directMenuVariantKey && !menuExperimentKey) {
    return;
  }

  if (
    directMenuVariantKey === NO_ACTIVE_MENU_VARIANT_KEY ||
    (!directMenuVariantKey && menuExperimentKey === NO_ACTIVE_MENU_VARIANT_KEY)
  ) {
    return;
  }

  // Legacy configurations point to another experiment. Newer configurations can assign the
  // menu variant directly from the ongoing menu experiment.
  const configuredMenuVariant = menuExperimentKey ? getVariantValue(menuExperimentKey) : undefined;
  const { key: configuredMenuVariantKey, value: configuredMenuVariantValue } =
    configuredMenuVariant || {};
  const hasConfiguredMenuVariant = Boolean(configuredMenuVariantKey || configuredMenuVariantValue);
  const isConfiguredDefaultMenu =
    configuredMenuVariantKey === NO_ACTIVE_MENU_VARIANT_KEY ||
    (!configuredMenuVariantKey && configuredMenuVariantValue === NO_ACTIVE_MENU_VARIANT_KEY);

  // After a failed evaluation, an absent nested value cannot prove that the selector was a
  // direct menu key. Preserve the safe default until a confirmed refresh settles the ambiguity.
  if (!hasConfiguredMenuVariant && hasAssignmentFetchFailed) {
    return;
  }

  if (isConfiguredDefaultMenu) {
    return {
      exposureFlagKey: menuExperimentKey ?? RunningExperiments.OngoingMenuExperiment,
      exposurePathname,
      isPending: false,
      variantName: undefined,
      variantNames: [],
    };
  }

  // Amplitude configurations in use encode the published menu name in either the variant key or
  // value. Preserve both as ordered candidates so consumers can select the artifact they publish.
  const variantNames = hasConfiguredMenuVariant
    ? getMenuVariantNames(configuredMenuVariantKey, configuredMenuVariantValue)
    : getMenuVariantNames(directMenuVariantKey, menuExperimentKey);
  return {
    exposureFlagKey: hasConfiguredMenuVariant
      ? menuExperimentKey ?? RunningExperiments.OngoingMenuExperiment
      : RunningExperiments.OngoingMenuExperiment,
    exposurePathname,
    isPending: false,
    variantName: variantNames[0],
    variantNames,
  };
};
